<template>
  <InteractionStep
    :disabled-next="!isAllRequiredFieldsFilled"
    :pending-next="pendingSave"
    @next="onSave">

    <template v-slot:content>
      <div class="step-information">
        <div v-if="showAddress" class="step-information__section  _align-top">
          <h3 class="step-information__section-title">{{ $tInteraction('Address for sending the invitation') }}</h3>
          <BaseRadioList :value="mailingTypeComputed"
                         :options="computedMailingTypes"
                         @input="onPostTypeChange"
                         text-key="name"
                         class="step-information__section-radio-list-toggle"/>
          <div class="step-information__row">
            <div class="step-information__col-6">
              <BaseInput :label="inputsNovaposhta.nova_poshta_fullname.name"
                         v-model="inputsNovaposhta.nova_poshta_fullname.value"
                         :error="inputsNovaposhta.nova_poshta_fullname.error"/>
            </div>
            <div class="step-information__col-6">
              <BaseInputPhone :label="inputsNovaposhta.nova_poshta_phone.name"
                              v-model="inputsNovaposhta.nova_poshta_phone.value"
                              :error="inputsNovaposhta.nova_poshta_phone.error"/>
            </div>
          </div>
          <template v-if="isNovaPoshta">
            <div class="step-information__row">
              <div class="step-information__col-6">
                <NovaPoshtaSettlement :label="inputsNovaposhta.nova_poshta_city.name"
                                      v-model="inputsNovaposhta.nova_poshta_city.value"
                                      @input="inputsNovaposhta.nova_poshta_number.value = null"
                                      :error="inputsNovaposhta.nova_poshta_city.error"/>
              </div>
              <div class="step-information__col-6">
                <NovaPoshtaWarehouses :label="inputsNovaposhta.nova_poshta_number.name"
                                      v-model="inputsNovaposhta.nova_poshta_number.value"
                                      :cityRef="cityRef"
                                      :cityName="cityName"
                                      :error="inputsNovaposhta.nova_poshta_number.error"/>
              </div>
            </div>
          </template>
          <template v-else>
            <div class="step-information__row">
              <div class="step-information__col-6">
                <BaseInput :label="$tInteraction('Country')"
                           v-model="inputsAddress.address_country.value"
                           :error="inputsAddress.address_country.error"/>
              </div>
              <div class="step-information__col-6">
                <BaseInput :label="$tInteraction('Region')"
                           v-model="inputsAddress.address_region.value"
                           :error="inputsAddress.address_region.error"/>
              </div>
            </div>
            <div class="step-information__row">
              <div class="step-information__col-6">
                <BaseInput :label="$tInteraction('District')"
                           :sub="'('+ $tInteraction('not necessary') +')'"
                           v-model="inputsAddress.address_district.value"
                           :error="inputsAddress.address_district.error"/>
              </div>
              <div class="step-information__col-6">
                <BaseInput :label="$tInteraction('Locality')"
                           v-model="inputsAddress.address_city.value"
                           :error="inputsAddress.address_city.error"/>
              </div>
            </div>
            <div class="step-information__row">
              <div class="step-information__col-6">
                <BaseInput :label="$tInteraction('Street')"
                           v-model="inputsAddress.address_street.value"
                           :error="inputsAddress.address_street.error"/>
              </div>
              <div class="step-information__col-6">
                <div class="step-information__col-small">
                  <BaseInput :label="$tInteraction('House')"
                             v-model="inputsAddress.address_house.value"
                             :error="inputsAddress.address_house.error"/>
                </div>
                <div class="step-information__col-small">
                  <BaseInput :label="$tInteraction('Flat')"
                             v-model="inputsAddress.address_apartment.value"
                             :error="inputsAddress.address_apartment.error"/>
                </div>
                <div class="step-information__col-small">
                  <BaseInput :label="$tInteraction('Index')"
                             v-model="inputsAddress.address_zip_code.value"
                             :error="inputsAddress.address_zip_code.error"
                             mask="#####"/>
                </div>
              </div>
            </div>
          </template>

        </div>
        <div v-if="showSection('clothing_size', 'waist', 'shoe_size', 'height', 'weight')"
             class="step-information__section  _align-top">
          <h3 class="step-information__section-title">{{ $tInteraction('Anthropometric data') }}</h3>
          <div class="step-information__row-wrap">
            <div v-if="inputs.clothing_size.isRequired"
                 class="step-information__col-3">
              <BaseSelect :label="inputs.clothing_size.name"
                          v-model="inputs.clothing_size.value"
                          :error="inputs.clothing_size.error"
                          :options="clothingSizes"
                          text-key="name"/>
            </div>
            <div v-if="inputs.waist.isRequired"
                 class="step-information__col-3">
              <BaseInput :label="inputs.waist.name"
                         v-model="inputs.waist.value"
                         :error="inputs.waist.error"
                         type="number"/>
            </div>
            <div v-if="inputs.shoe_size.isRequired"
                 class="step-information__col-3">
              <BaseInput :label="inputs.shoe_size.name"
                         v-model="inputs.shoe_size.value"
                         :error="inputs.shoe_size.error"
                         type="number"/>
            </div>
            <div v-if="inputs.height.isRequired"
                 class="step-information__col-3">
              <BaseInput :label="inputs.height.name"
                         v-model="inputs.height.value"
                         :error="inputs.height.error"
                         type="number"/>
            </div>
            <div v-if="inputs.weight.isRequired"
                 class="step-information__col-3">
              <BaseInput :label="inputs.weight.name"
                         v-model="inputs.weight.value"
                         :error="inputs.weight.error"
                         type="number"/>
            </div>
          </div>
        </div>
      </div>
      <BaseAlert :value="error"
                 class="step-information__error  _error"/>
    </template>
  </InteractionStep>
</template>

<script>
  import {http} from '@/axios'
  import InteractionStep from '../InteractionStep'
  import interactionStepMixin from "@/mixins/interactionStepMixin"
  import NovaPoshtaSettlement from "@/components/novaPoshtaAddress/NovaPoshtaSettlement"
  import NovaPoshtaWarehouses from "@/components/novaPoshtaAddress/NovaPoshtaWarehouses"

  const MAILING_TYPE_UKRPOSHTA = 'UKRPOSHTA'
  const MAILING_TYPE_NOVAPOSHTA = 'NOVAPOSHTA'
  const mailingTypes = [{
    id: 1,
    type: MAILING_TYPE_NOVAPOSHTA,
    name: 'Нова Пошта'
  }, {
    id: 2,
    type: MAILING_TYPE_UKRPOSHTA,
    name: 'Укрпошта'
  }]


  export default {
    mixins: [interactionStepMixin],
    components: {
      InteractionStep,
      NovaPoshtaSettlement,
      NovaPoshtaWarehouses,
    },
    data() {
      return {
        pendingSave: false,
        error: null,
        value: null,
        message: null,
        MAILING_TYPE_UKRPOSHTA,
        MAILING_TYPE_NOVAPOSHTA,
        mailingType: null,
        inputsAddress: {
          address_country: {
            isRequired: true,
            value: null
          },
          address_region: {
            isRequired: true,
            value: null
          },
          address_district: {
            isRequired: false,
            value: null
          },
          address_city: {
            isRequired: true,
            value: null
          },
          address_street: {
            isRequired: true,
            value: null
          },
          address_house: {
            isRequired: true,
            value: null
          },
          address_apartment: {
            isRequired: false,
            value: null
          },
          address_zip_code: {
            isRequired: true,
            value: null
          },
        },
        inputs: {
          shoe_size: {
            name: this.$tInteraction('Shoe size'),
            isRequired: true,
            value: null
          },
          clothing_size: {
            name: this.$tInteraction('Size of shirts / T-shirts'),
            isRequired: true,
            value: null
          },
          waist: {
            name: this.$tInteraction('Waist, cm'),
            isRequired: true,
            value: null
          },
          height: {
            name: this.$tInteraction('Growth, cm'),
            isRequired: true,
            value: null
          },
          weight: {
            name: this.$tInteraction('Weight, kg'),
            isRequired: true,
            value: null
          }
        },
        inputsNovaposhta: {
          nova_poshta_fullname: {
            name: this.$tInteraction('Full name for delivery'),
            isRequired: true,
            value: null
          },
          nova_poshta_phone: {
            name: this.$tInteraction('Phone for delivery'),
            isRequired: true,
            value: null
          },
          nova_poshta_city: {
            name: this.$tInteraction('City for delivery'),
            isRequired: true,
            value: null
          },
          nova_poshta_number: {
            name: this.$tInteraction('Department number for delivery'),
            isRequired: true,
            value: null
          }
        }
      }
    },
    computed: {
      computedMailingTypes() {
        return mailingTypes.map(el => {
          el = {...el}
          el.name = this.$tInteraction(el.name)
          return el
        })
      },
      mailingTypeComputed() {
        return this.mailingType || this.computedMailingTypes[0]
      },
      isNovaPoshta() {
        return this.mailingTypeComputed.type === MAILING_TYPE_NOVAPOSHTA
      },
      cityRef() {
        return this.$get(this.inputsNovaposhta.nova_poshta_city.value, 'DeliveryCity') //Ref
      },
      cityName() {
        return this.$get(this.inputsNovaposhta.nova_poshta_city.value, 'Present', this.inputsNovaposhta.nova_poshta_city.value)
      },

      clothingSizes() {
        return this.$store.state.interaction.clothingSizes
      },
      showAddress() {
        const data = this.$store.state.interaction.stepData.data
        return !!data.documents_delivery_address
      },
      showNovaPoshta() {
        const data = this.$store.state.interaction.stepData.data
        return !!data.nova_poshta
      },
      inputsAll() {
        return Object.assign({}, this.inputsAddress, this.inputs, this.inputsNovaposhta)
      },
      isAllRequiredFieldsFilled() {
        const inputs = this.inputsAll

        for (const key in inputs) {
          const input = inputs[key]

          if (input.isRequired && !input.value) {
            return false
          }
        }

        return true
      }
    },
    methods: {
      onPostTypeChange(type) {
        this.mailingType = type

        if (type.type === MAILING_TYPE_NOVAPOSHTA) {
          this.$set(this.inputsNovaposhta['nova_poshta_city'], 'isRequired', true)
          this.$set(this.inputsNovaposhta['nova_poshta_number'], 'isRequired', true)

          for (const key in this.inputsAddress) {
            this.$set(this.inputsAddress[key], 'isRequired', false)
          }
        } else {
          this.$set(this.inputsNovaposhta['nova_poshta_city'], 'isRequired', false)
          this.$set(this.inputsNovaposhta['nova_poshta_number'], 'isRequired', false)

          for (const key in this.inputsAddress) {
            this.$set(this.inputsAddress[key], 'isRequired', true)
          }
        }
      },
      onSave() {
        this.error = null
        this.message = null

        let hasError = false
        const step = this.$store.state.interaction.step
        const sendData = {
          mailing_type: this.mailingTypeComputed.id
        }

        const inputs = this.inputsAll
        for (const key in inputs) {
          const input = inputs[key]

          if (input.isRequired && !input.value) {
            this.$set(inputs[key], 'error', this.$tGlobal('This field is required'))
            hasError = true
          } else {
            this.$set(inputs[key], 'error', null)
          }

          sendData[key] = input.value
        }

        sendData.clothing_size = sendData.clothing_size ? sendData.clothing_size.id : null
        sendData.nova_poshta_city = this.$get(sendData.nova_poshta_city, 'Present', sendData.nova_poshta_city || null)
        sendData.nova_poshta_number = this.$get(sendData.nova_poshta_number, 'Description', sendData.nova_poshta_number || null)

        if (hasError) {
          return this.error = this.$tGlobal('All required fields must be filled')
        }

        this.pendingSave = true

        http.post(`/api/new-interaction/${this.$route.params.id}/${step}`, sendData).then(async () => {
          await this.$store.dispatch('interaction/loadInteraction')
        }).catch(e => {
          this.handleInteractionError(e)
        }).finally(() => {
          this.pendingSave = false
        })
      },
      showSection(...keys) {
        const inputs = this.inputs
        return keys.some(key => inputs[key].isRequired)
      }
    },
    created() {
      const data = this.$store.state.interaction.stepData.data
      const resume = this.$store.state.interaction.resume.resume_data
      const inputs = this.inputs
      const inputsAddress = this.inputsAddress
      const inputsNovaposhta = this.inputsNovaposhta

      for (const key in inputs) {
        inputs[key].isRequired = !!data[key]
      }

      if (!data.documents_delivery_address) {
        for (const key in inputsAddress) {
          inputsAddress[key].isRequired = false
        }

        for (const key in inputsNovaposhta) {
          inputsNovaposhta[key].isRequired = false
        }
      } else {
        inputsNovaposhta.nova_poshta_fullname.value = `${resume[1].surname} ${resume[1].name}`
        inputsNovaposhta.nova_poshta_phone.value = resume[1].phones[0] ? resume[1].phones[0].phone : null

        this.onPostTypeChange(this.computedMailingTypes[0])
      }
    }
  }
</script>

<style>

</style>
